import { LocalityFilter } from 'rentalui-finance';
import { useLocation as useRecoveryLocation } from 'react-router-dom';
import { client as recoveryClient } from '../api';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Badge, Button, Card, Form, Spinner, Tab } from 'react-bootstrap';
import MySubscriptionPanel from '../components/MySubscriptionPanel';
import TrialNudgeModal from '../components/TrialNudgeModal';
import { usePagedArray, Pager } from 'rentalui-finance';
import ResponsiveTable from '../components/ResponsiveTable';
import DashboardTabs from '../components/DashboardTabs';
import EmailsPanel from '../components/EmailsPanel';
import InvoicesPanel from '../components/InvoicesPanel';
import { updateItem, getMyBookings, getMyItems, imageUrl, resolveExtension, markRefundSent } from '../api';
import BookingActions from '../components/BookingActions';
import CancelBookingModal from '../components/CancelBookingModal';
import CancellationDetailsModal from '../components/CancellationDetailsModal';
import ProductDetailsModal from '../components/ProductDetailsModal';
import AssignDeliveryModal from '../components/AssignDeliveryModal';
import DeliveryList from '../components/DeliveryList';
import MarkRefundSentModal from '../components/MarkRefundSentModal';
import OrderDetailsModal from '../components/OrderDetailsModal';
import MarkPaymentReceivedModal from '../components/MarkPaymentReceivedModal';
import OwnerBusinessInsights from '../components/OwnerBusinessInsights';
import RequestDeliveryPersonModal from '../components/RequestDeliveryPersonModal';
import OwnerPayablesPanel from '../components/OwnerPayablesPanel';
import InterBusinessChargesPanel from '../components/InterBusinessChargesPanel';
import PickupAddressCard from '../components/PickupAddressCard';
import { listMyDeliveryPersonRequests, getLowRatings, getOwnerFinanceSurface } from '../api';
import StarRating from '../components/StarRating';
import InfoTip from '../components/InfoTip';
import { DEPOSIT_STATUS_HELP, RETURN_STATUS_HELP, DELIVERY_STATUS_HELP } from '../components/statusHelp';

// Owner Bookings & Cancellation v2 doc, Story 13b - these are the states where the customer has
// already scheduled (or handed over) a return and is now waiting on the owner to act, so they sort
// to the top of the Booking Requests list ahead of everything else.
const RETURN_DUE_STATUSES = ['RETURN_SCHEDULED', 'PICKED_UP'];
// Story 13c - once a booking's return is fully COMPLETED it moves out of the active list into its
// own "Completed Orders" tab, split by how recently it wrapped up.
const RECENT_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;
// Story 15 - a deposit refund that's still REFUND_PENDING (return confirmed, or the booking was
// cancelled, but the money hasn't actually reached the customer yet) keeps the order out of
// Completed regardless of returnStatus/status - see the activeBookings/completed split below.
function needsRefund(booking) {
  return booking.depositAmount > 0 && booking.depositStatus === 'REFUND_PENDING';
}
// A cancelled booking nobody still has to act on - no deposit refund outstanding and no
// owner-recorded payment still awaiting finance reconciliation. These move out of the active
// "Booking Requests" list into their own "Cancelled" tab; a cancelled booking that still needs a
// refund sent (or reconciled) stays in Booking Requests so it can't be missed.
function isResolvedCancellation(booking) {
  return booking.status === 'cancelled' && !needsRefund(booking) && !booking.payment?.needsReconciliation;
}
// Story 13d - earnings period filter options.
const EARNINGS_PERIODS = [
  { key: 'all', label: 'All time' },
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'This week' },
  { key: 'month', label: 'This month' },
  { key: 'year', label: 'This year' }
];

// Lower number = higher priority (sorts first). Return-due and refund-pending bookings both need
// the owner to actually go do something (collect the item / send the refund); pending ones need
// an accept/decline; all three outrank a routine in-progress booking.
function bookingPriority(booking) {
  if (RETURN_DUE_STATUSES.includes(booking.returnStatus) || needsRefund(booking)) return 0;
  if (booking.status === 'pending') return 1;
  return 2;
}

// [start, end) for the selected earnings period, in the browser's local time; null means "all
// time" (no filtering). Weeks start Monday.
function periodBounds(period) {
  const now = new Date();
  if (period === 'today') {
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return [start, new Date(start.getTime() + 86400000)];
  }
  if (period === 'week') {
    const day = now.getDay();
    const diffToMonday = (day === 0 ? -6 : 1) - day;
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() + diffToMonday);
    return [start, new Date(start.getTime() + 7 * 86400000)];
  }
  if (period === 'month') {
    return [new Date(now.getFullYear(), now.getMonth(), 1), new Date(now.getFullYear(), now.getMonth() + 1, 1)];
  }
  if (period === 'year') {
    return [new Date(now.getFullYear(), 0, 1), new Date(now.getFullYear() + 1, 0, 1)];
  }
  return null;
}

export default function OwnerDashboard() {
  const [locality, setLocality] = useState('');
  const recoveryLocation = useRecoveryLocation();
  useEffect(() => {
    const params = new URLSearchParams(recoveryLocation.search);
    const id = params.get('recoveryCase');
    const bookingId = params.get('bookingId');
    if (!id && !bookingId) return undefined;
    let alive = true;
    recoveryClient.get(bookingId ? `/api/order-attention/orders/jewelry/${encodeURIComponent(bookingId)}` : `/api/order-attention/${id}/details`).then(response => {
      if (bookingId) response = { data: { data: { order: response.data.data } } };
      if (alive && response.data.data.order) setOrderDetailsBooking(response.data.data.order);
    }).catch(() => { if (alive) setError('Unable to open the requested order. Return to its recovery page and retry.'); });
    return () => { alive = false; };
  }, [recoveryLocation.search]);

  const [items, setItems] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cancelBookingId, setCancelBookingId] = useState(null); // Story 4: owner-initiated cancel
  const [viewCancellationBooking, setViewCancellationBooking] = useState(null); // Story 12
  const [earningsPeriod, setEarningsPeriod] = useState('all'); // Story 13d
  const [productItemId, setProductItemId] = useState(null); // Story 14
  const [assignDeliveryBooking, setAssignDeliveryBooking] = useState(null); // Story 14
  const [refundSentBookingId, setRefundSentBookingId] = useState(null); // Story 15
  const [orderDetailsBooking, setOrderDetailsBooking] = useState(null); // Story 13d - earnings row order popup
  const [paymentBooking, setPaymentBooking] = useState(null); // payements_requirements.md - gateway-down manual payment
  const [showDeliveryRequest, setShowDeliveryRequest] = useState(false); // user_management_and_delivery_kyc.md
  const [myDeliveryRequests, setMyDeliveryRequests] = useState([]);
  const [lowRatings, setLowRatings] = useState([]); // fix_issue3.md #3
  const [activeTab, setActiveTab] = useState('items');
  const [financeSurface, setFinanceSurface] = useState({ delivery_payments: false, b2b_billing: false });

  // Promise.allSettled, not Promise.all - a failure on one call (e.g. bookings) shouldn't hide
  // data that did load successfully from the other (e.g. items). Previously one endpoint
  // erroring meant the whole dashboard showed a blank "Could not load" message even though the
  // other half of the data was available.
  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    const [itemsResult, bookingsResult] = await Promise.allSettled([getMyItems(), getMyBookings()]);
    if (itemsResult.status === 'fulfilled') setItems(itemsResult.value);
    if (bookingsResult.status === 'fulfilled') setBookings(bookingsResult.value);

    const failed = [];
    if (itemsResult.status === 'rejected') failed.push('listings');
    if (bookingsResult.status === 'rejected') failed.push('bookings');
    if (failed.length > 0) {
      setError(`Could not load your ${failed.join(' or ')}. Showing what did load.`);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const loadDeliveryRequests = useCallback(() => {
    listMyDeliveryPersonRequests().then(setMyDeliveryRequests).catch(() => {});
  }, []);
  useEffect(() => { loadDeliveryRequests(); }, [loadDeliveryRequests]);

  useEffect(() => {
    getLowRatings().then(setLowRatings).catch(() => {});
  }, []);
  useEffect(() => {
    getOwnerFinanceSurface().then(setFinanceSurface).catch(() => {});
  }, []);

  // fix_issues.md #6 - a listing is taken off the public site by disabling it, not by deleting it
  // outright; disabling and re-enabling both just PATCH status (see common/catalogRepository.js's
  // list() for where 'disabled' is actually excluded from public browsing).
  const handleToggleStatus = async (item) => {
    const nextStatus = item.status === 'disabled' ? 'available' : 'disabled';
    await updateItem(item._id, { status: nextStatus });
    load();
  };

  const handleExtensionDecision = async (id, status) => {
    await resolveExtension(id, status);
    load();
  };

  const itemName = (itemId) => items.find((i) => i._id === itemId)?.name || 'Item';

  // Story 13b/13c/15: active bookings (everything except a fully-wrapped-up return), sorted so
  // return-due/refund-pending ones surface first; and the completed bucket, split by recency.
  // Story 15: a returnStatus: COMPLETED booking whose deposit refund is still REFUND_PENDING
  // stays in "active" (with a "Refund Pending" badge - see renderBookingStatus) instead of
  // Completed, so the refund can't get forgotten just because the item itself came back.
  const { activeBookings, cancelledBookings, recentCompleted, olderCompleted } = useMemo(() => {
    const active = [];
    const cancelled = [];
    const completed = [];
    for (const booking of bookings) {
      if (isResolvedCancellation(booking)) { cancelled.push(booking); continue; }
      (booking.returnStatus === 'COMPLETED' && !needsRefund(booking) ? completed : active).push(booking);
    }
    active.sort((a, b) => bookingPriority(a) - bookingPriority(b));
    cancelled.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
    completed.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
    const cutoff = Date.now() - RECENT_WINDOW_MS;
    return {
      activeBookings: active,
      cancelledBookings: cancelled,
      recentCompleted: completed.filter((b) => new Date(b.updatedAt).getTime() >= cutoff),
      olderCompleted: completed.filter((b) => new Date(b.updatedAt).getTime() < cutoff)
    };
  }, [bookings]);

  // Client-side pagination per list (bucketing + priority sort above happen in the browser).
  const localityOptions = [...new Set(items.map(item => item.location).filter(Boolean))];
  const matchesLocality = row => !locality || String(row.itemLocation || row.location || items.find(item => String(item._id) === String(row.itemId))?.location || '').toLowerCase().includes(locality.toLowerCase());
  const activePaged = usePagedArray(activeBookings.filter(matchesLocality), { resetKey: `${activeTab}:${locality}` });
  const cancelledPaged = usePagedArray(cancelledBookings.filter(matchesLocality), { resetKey: `${activeTab}:${locality}` });
  const recentPaged = usePagedArray(recentCompleted.filter(matchesLocality), { resetKey: `${activeTab}:${locality}` });
  const olderPaged = usePagedArray(olderCompleted.filter(matchesLocality), { resetKey: `${activeTab}:${locality}` });
  const itemsPaged = usePagedArray(items.filter(matchesLocality), { resetKey: `${activeTab}:${locality}` });
  // Story 13d: cancelled bookings never actually pay out (payoutStatus never leaves NOT_DUE - see
  // bookingRepository.cancel()), so they're excluded here rather than counted as earned.
  const earningsCandidates = useMemo(
    () => bookings.filter((b) => b.rentalAmount > 0 && b.status !== 'cancelled'),
    [bookings]
  );
  const filteredEarnings = useMemo(() => {
    const bounds = periodBounds(earningsPeriod);
    if (!bounds) return earningsCandidates;
    return earningsCandidates.filter((b) => {
      const end = new Date(b.endDate).getTime();
      return end >= bounds[0].getTime() && end < bounds[1].getTime();
    });
  }, [earningsCandidates, earningsPeriod]);
  const totalEarnings = filteredEarnings.reduce((sum, b) => sum + (b.ownerEarnings || 0), 0);
  const earningsPaged = usePagedArray(filteredEarnings, { resetKey: `${activeTab}:${earningsPeriod}` });

  // Shared between the desktop table and the mobile card list (Story 5) so the action/status
  // logic exists in exactly one place instead of two copies that could drift apart.
  const renderBookingStatus = (booking, compact = false) => (
    <div className={compact ? 'text-end' : ''}>
      <Badge bg={booking.status === 'confirmed' ? 'success' : booking.status === 'cancelled' ? 'danger' : 'warning'}>
        {booking.status}
      </Badge>
      {RETURN_DUE_STATUSES.includes(booking.returnStatus) && (
        <InfoTip text="The customer has scheduled or handed over the return — it's waiting on you to collect / confirm it.">
          <Badge bg="info" className="ms-1">Return due</Badge>
        </InfoTip>
      )}
      {needsRefund(booking) && (
        // Story 15 - visible regardless of booking.status (a cancelled booking can have a
        // pending refund too), which is why this lives outside the confirmed/cancelled branches.
        <InfoTip text={DEPOSIT_STATUS_HELP.REFUND_PENDING}>
          <Badge bg="warning" text="dark" className="ms-1">Refund Pending</Badge>
        </InfoTip>
      )}
      {booking.extension?.requested && booking.extension.status === 'pending' && (
        <div className="mt-1">
          <small className="d-block text-muted">Extension requested to {new Date(booking.extension.requestedEndDate).toLocaleDateString()}</small>
          <Button size="sm" variant="success" className="me-1" onClick={() => handleExtensionDecision(booking._id, 'approved')}>Approve</Button>
          <Button size="sm" variant="outline-danger" onClick={() => handleExtensionDecision(booking._id, 'rejected')}>Reject</Button>
        </div>
      )}
    </div>
  );

  const renderFulfillment = (booking) => booking.status === 'confirmed' && (
    <>
      <div className="small text-muted">
        Delivery: {booking.deliveryStatus.replace(/_/g, ' ')}
        <InfoTip text={DELIVERY_STATUS_HELP[booking.deliveryStatus]} />
      </div>
      <div className="small text-muted">
        Return: {booking.returnStatus.replace(/_/g, ' ')}
        <InfoTip text={RETURN_STATUS_HELP[booking.returnStatus]} />
      </div>
      {booking.deliveryAssignment?.assignedTo && (
        // Story 14 - who's delivering, and when.
        <div className="small text-muted">
          Assigned: {booking.deliveryAssignment.assignedTo.name}
          {booking.deliveryAssignment.scheduledAt && ` (${new Date(booking.deliveryAssignment.scheduledAt).toLocaleString()})`}
          {booking.deliveryAssignment.autoAssigned && <Badge bg="info" text="dark" className="ms-1">Auto-assigned</Badge>}
        </div>
      )}
      {/* Story 17 - a delivery person declined/is unavailable; needs a replacement. */}
      {booking.deliveryNeedsReassignment && (
        <div className="small"><Badge bg="warning" text="dark">Needs reassignment</Badge></div>
      )}
    </>
  );

  // v3_fix_issues #12 - the action set now lives in the shared BookingActions component (so the
  // website admin gets the same one). This just wires it to the owner's modals + refetch.
  const renderBookingActions = (booking) => (
    <BookingActions
      booking={booking}
      onChanged={load}
      onViewProduct={setProductItemId}
      onRecordPayment={setPaymentBooking}
      onMarkRefundSent={setRefundSentBookingId}
      onViewCancellation={setViewCancellationBooking}
      onAssignDelivery={setAssignDeliveryBooking}
      onCancel={setCancelBookingId}
    />
  );

  if (loading) return <Spinner animation="border" role="status" />;

  // Shared list renderer for the active list and the completed-orders groups. ResponsiveTable
  // gives one column spec that renders a table on desktop and stacked cards on a phone.
  const renderBookingsList = (paged, emptyMessage) => {
    if (paged.total === 0) return <Alert variant="light">{emptyMessage}</Alert>;
    return (
      <>
        <ResponsiveTable
          rows={paged.rows}
          rowKey={(b) => b._id}
          empty={emptyMessage}
          columns={[
            {
              key: 'order', header: 'Order', card: 'title',
              cell: (booking) => (
                <Button
                  variant="link" size="sm"
                  className="p-0 align-baseline font-monospace"
                  title={booking._id}
                  onClick={() => setOrderDetailsBooking(booking)}
                >
                  #{booking._id.slice(-6).toUpperCase()}
                </Button>
              )
            },
            { key: 'item', header: 'Item', card: 'subtitle', cell: (booking) => itemName(booking.itemId) },
            {
              key: 'requestedBy', header: 'Requested by',
              cell: (booking) => (
                <>{booking.customerName}<br /><small className="text-muted">{booking.email} &middot; {booking.phoneNumber}</small></>
              )
            },
            {
              key: 'dates', header: 'Dates',
              cell: (booking) => `${new Date(booking.startDate).toLocaleDateString()} – ${new Date(booking.endDate).toLocaleDateString()}`
            },
            { key: 'status', header: 'Status', cell: (booking) => renderBookingStatus(booking) },
            { key: 'fulfillment', header: 'Fulfillment', hideEmpty: true, cell: (booking) => renderFulfillment(booking) }
          ]}
          actions={(booking) => renderBookingActions(booking)}
        />
        <Pager {...paged} />
      </>
    );
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">Owner dashboard</h3>
        <Button as={Link} to="/owner/items/new">
          + List a piece
        </Button>
      </div>
      {error && <Alert variant="danger">{error}</Alert>}

      {['items', 'properties', 'bookings', 'completed', 'cancelled', 'deliveries', 'today', 'visits'].includes(activeTab) && <LocalityFilter value={locality} options={localityOptions} onChange={setLocality} />}
      <TrialNudgeModal listingCount={items.length} onActivated={load} />

      {/* fix_issue3.md #3 - worst ratings on this owner's listings, surfaced for them to act on. */}
      {lowRatings.length > 0 && (
        <Alert variant="warning">
          <strong>{lowRatings.length} low rating{lowRatings.length > 1 ? 's' : ''} need your attention</strong>
          <ul className="mb-0 mt-2 small">
            {lowRatings.slice(0, 5).map((r) => (
              <li key={r._id}>
                <StarRating value={r.rating} /> {r.itemName ? `— ${r.itemName}` : ''}
                {r.review ? `: "${r.review}"` : ''}
              </li>
            ))}
          </ul>
        </Alert>
      )}

      <DashboardTabs
        activeKey={activeTab} onSelect={setActiveTab}
        primary={[
          ['bookings', `Booking Requests (${activeBookings.length})`],
          ['items', `My Pieces (${items.length})`],
          ['deliveries', 'Due Deliveries']
        ]}
        more={[
          ['completed', `Completed Orders (${recentCompleted.length + olderCompleted.length})`],
          ['cancelled', `Cancelled (${cancelledBookings.length})`],
          ['earnings', 'Earnings'],
          ...(financeSurface.delivery_payments ? [['delivery-payments', 'Delivery payments']] : []),
          ...(financeSurface.b2b_billing ? [['b2b-delivery', 'Shared-delivery billing']] : []),
          ['business', 'Business'],
          ['pickup', 'Pickup address'],
          ['subscription', 'Subscription'],
          ['invoices', 'Invoices'],
          ['emails', 'Emails']
        ]}
      >
        <Tab.Pane eventKey="items">
          {itemsPaged.total === 0 ? (
            <Alert variant="light">You haven't listed any pieces yet.</Alert>
          ) : (
            <div className="row g-3">
              {itemsPaged.rows.map((item) => (
                <div className="col-md-4" key={item._id}>
                  <Card className="h-100">
                    {item.images?.[0] && (
                      <Card.Img variant="top" src={imageUrl(item.images[0])} style={{ height: 140, objectFit: 'cover' }} />
                    )}
                    <Card.Body>
                      <Card.Title as="h6">{item.name}</Card.Title>
                      <Card.Text className="text-muted small">
                        {item.location} &middot; ₹{item.price}/{item.priceUnit}
                      </Card.Text>
                      <Badge bg={item.status === 'available' ? 'success' : 'secondary'} className="mb-2">
                        {item.status}
                      </Badge>
                      <div className="d-flex gap-2">
                        <Button size="sm" variant="outline-secondary" as={Link} to={`/owner/items/${item._id}/edit`}>
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant={item.status === 'disabled' ? 'outline-success' : 'outline-danger'}
                          onClick={() => handleToggleStatus(item)}
                        >
                          {item.status === 'disabled' ? 'Enable' : 'Disable'}
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </div>
              ))}
            </div>
          )}
          <Pager {...itemsPaged} />
        </Tab.Pane>

        {/* Owner's own pickup / business address - moved out of "My Pieces" into its own
            "More" tab so it isn't in the way of the day-to-day listing view. */}
        <Tab.Pane eventKey="pickup">
          <PickupAddressCard />
        </Tab.Pane>

        <Tab.Pane eventKey="subscription" mountOnEnter unmountOnExit>
          <MySubscriptionPanel />
        </Tab.Pane>

        {/* delivery_person_payables.md - what this owner owes delivery people per completed leg. */}
        <Tab.Pane eventKey="delivery-payments" mountOnEnter unmountOnExit>
          <OwnerPayablesPanel />
        </Tab.Pane>

        {/* shared_delivery_inter_business_billing.md - inter-business fees for shared delivery people. */}
        <Tab.Pane eventKey="b2b-delivery" mountOnEnter unmountOnExit>
          <InterBusinessChargesPanel />
        </Tab.Pane>

        {/* Story 13b: sorted so return-due bookings (scheduled/picked-up, awaiting the owner)
            surface first. Story 13c: fully completed returns live in their own tab below instead
            of here. */}
        <Tab.Pane eventKey="bookings">
          {renderBookingsList(activePaged, 'No booking requests yet.')}
        </Tab.Pane>

        <Tab.Pane eventKey="completed">
          {recentCompleted.length === 0 && olderCompleted.length === 0 ? (
            <Alert variant="light">No completed orders yet.</Alert>
          ) : (
            <>
              <h6 className="text-uppercase text-muted small mb-2">Recent (last 30 days)</h6>
              {renderBookingsList(recentPaged, 'Nothing completed in the last 30 days.')}
              <h6 className="text-uppercase text-muted small mb-2 mt-4">Older</h6>
              {renderBookingsList(olderPaged, 'Nothing older.')}
            </>
          )}
        </Tab.Pane>

        {/* Cancelled bookings that no role still needs to act on - moved out of Booking Requests
            so that list only holds orders that actually need attention. */}
        <Tab.Pane eventKey="cancelled">
          {renderBookingsList(cancelledPaged, 'No cancelled bookings.')}
        </Tab.Pane>

        {/* v3_fix_issues #7 & #8 (owner side) - your own business insights: KPIs vs. the
            previous period + earnings/bookings over time + best listing/customer. Computed from
            the bookings already loaded (your listings only). */}
        <Tab.Pane eventKey="business">
          <OwnerBusinessInsights bookings={bookings} items={items} />
        </Tab.Pane>

        <Tab.Pane eventKey="earnings">
          <Form.Select
            size="sm" style={{ maxWidth: 220 }} className="mb-3"
            value={earningsPeriod} onChange={(e) => setEarningsPeriod(e.target.value)}
          >
            {EARNINGS_PERIODS.map((p) => (
              <option key={p.key} value={p.key}>{p.label}</option>
            ))}
          </Form.Select>
          {filteredEarnings.length === 0 ? (
            <Alert variant="light">No priced bookings in this period.</Alert>
          ) : (
            <>
              <Alert variant="light" className="fw-semibold">
                Total earnings ({EARNINGS_PERIODS.find((p) => p.key === earningsPeriod)?.label.toLowerCase()}): ₹{totalEarnings.toLocaleString('en-IN')}
              </Alert>
              <ResponsiveTable
                rows={earningsPaged.rows}
                rowKey={(b) => b._id}
                empty="No priced bookings in this period."
                columns={[
                  {
                    key: 'order', header: 'Order', card: 'title',
                    cell: (booking) => (
                      <>
                        <Button
                          variant="link" size="sm"
                          className="p-0 align-baseline font-monospace"
                          title={booking._id}
                          onClick={() => setOrderDetailsBooking(booking)}
                        >
                          #{booking._id.slice(-6).toUpperCase()}
                        </Button>
                        {booking.customerName && (<><br /><small className="text-muted">{booking.customerName}</small></>)}
                      </>
                    )
                  },
                  {
                    key: 'item', header: 'Item', card: 'subtitle',
                    cell: (booking) => (
                      <>
                        <Link to={`/items/${booking.itemId}`}>{itemName(booking.itemId)}</Link>
                        {' '}
                        <Button variant="link" size="sm" className="p-0 align-baseline" onClick={() => setProductItemId(booking.itemId)}>
                          details
                        </Button>
                      </>
                    )
                  },
                  {
                    key: 'dates', header: 'Dates', className: 'text-nowrap',
                    cell: (booking) => (<small>{new Date(booking.startDate).toLocaleDateString()} – {new Date(booking.endDate).toLocaleDateString()}</small>)
                  },
                  { key: 'rental', header: 'Rental Amount', cell: (booking) => `₹${booking.rentalAmount.toLocaleString('en-IN')}` },
                  { key: 'commission', header: 'Booking commission', cell: (booking) => `${Math.round(booking.commissionRate * 100)}%` },
                  { key: 'earnings', header: 'Your Earnings', cell: (booking) => `₹${booking.ownerEarnings.toLocaleString('en-IN')}` },
                  { key: 'payout', header: 'Payout to you', card: 'badge', cell: (booking) => <Badge bg="secondary">{booking.payoutStatus.replace(/_/g, ' ')}</Badge> }
                ]}
              />
              <Pager {...earningsPaged} />
            </>
          )}
        </Tab.Pane>

        {/* Story 14 - owner's scope: deliveries for their own listings, searchable/paginated. */}
        <Tab.Pane eventKey="deliveries">
          {/* user_management_and_delivery_kyc.md - the owner can't create delivery accounts; they
              ask the website owner to onboard one (with the mandatory Aadhaar KYC + address). */}
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <Button size="sm" variant="outline-primary" onClick={() => setShowDeliveryRequest(true)}>
              + Request a delivery person
            </Button>
            {myDeliveryRequests.length > 0 && (
              <div className="small text-muted">
                Your requests:{' '}
                {myDeliveryRequests.slice(0, 5).map((r) => (
                  <span key={r._id} className="ms-1">
                    {r.name} <Badge bg={r.status === 'approved' ? 'success' : r.status === 'rejected' ? 'danger' : 'warning'} text={r.status === 'pending' ? 'dark' : undefined}>{r.status}</Badge>
                  </span>
                ))}
              </div>
            )}
          </div>
          <DeliveryList showSearch />
        </Tab.Pane>

        <Tab.Pane eventKey="invoices" mountOnEnter unmountOnExit>
          <InvoicesPanel />
        </Tab.Pane>

        <Tab.Pane eventKey="emails" mountOnEnter unmountOnExit>
          <EmailsPanel scope="owner" />
        </Tab.Pane>
      </DashboardTabs>

      <CancelBookingModal
        bookingId={cancelBookingId}
        show={!!cancelBookingId}
        onHide={() => setCancelBookingId(null)}
        onCancelled={() => { setCancelBookingId(null); load(); }}
      />
      <CancellationDetailsModal
        booking={viewCancellationBooking}
        show={!!viewCancellationBooking}
        onHide={() => setViewCancellationBooking(null)}
      />
      <ProductDetailsModal itemId={productItemId} show={!!productItemId} onHide={() => setProductItemId(null)} />
      <AssignDeliveryModal
        booking={assignDeliveryBooking}
        show={!!assignDeliveryBooking}
        onHide={() => setAssignDeliveryBooking(null)}
        onAssigned={() => { setAssignDeliveryBooking(null); load(); }}
      />
      <MarkRefundSentModal
        bookingId={refundSentBookingId}
        show={!!refundSentBookingId}
        onHide={() => setRefundSentBookingId(null)}
        onConfirm={markRefundSent}
        onSent={() => { setRefundSentBookingId(null); load(); }}
      />
      <OrderDetailsModal
        booking={orderDetailsBooking}
        itemName={orderDetailsBooking ? itemName(orderDetailsBooking.itemId) : ''}
        show={!!orderDetailsBooking}
        onHide={() => setOrderDetailsBooking(null)}
        onChanged={() => { load(); setOrderDetailsBooking(null); }}
        onViewProduct={setProductItemId}
        onRecordPayment={(b) => { setOrderDetailsBooking(null); setPaymentBooking(b); }}
        onMarkRefundSent={(id) => { setOrderDetailsBooking(null); setRefundSentBookingId(id); }}
        onViewCancellation={(b) => { setOrderDetailsBooking(null); setViewCancellationBooking(b); }}
        onAssignDelivery={(b) => { setOrderDetailsBooking(null); setAssignDeliveryBooking(b); }}
        onCancel={(id) => { setOrderDetailsBooking(null); setCancelBookingId(id); }}
      />
      <MarkPaymentReceivedModal
        booking={paymentBooking}
        show={!!paymentBooking}
        onHide={() => setPaymentBooking(null)}
        onRecorded={() => { setPaymentBooking(null); load(); }}
      />
      <RequestDeliveryPersonModal
        show={showDeliveryRequest}
        onHide={() => setShowDeliveryRequest(false)}
        onSubmitted={() => { setShowDeliveryRequest(false); loadDeliveryRequests(); }}
      />
    </div>
  );
}
